import React, { ReactElement, useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useRouter } from 'next/router';
import Link from 'next/link';
import classNames from 'classnames/bind';
import RootLayout from 'layouts/RootLayout';
import reCaptcha from 'utils/reCaptcha';
import useWindowSize from 'utils/hooks/useWindowSize';
import { freePackageItems, premiumPackageItems, premiumPlusPackageItems, PackageItem, PackageType } from 'sections/MembershipPage/data';
import Breadcrumbs from 'components/Breadcrumbs';
import CustomTooltip from 'sections/MembershipPage/components/Tooltip';
import PackageItemElement from 'sections/MembershipPage/components/PackageItem';
import styles from 'styles/pages/Membership/Membership.module.scss';

const cn = classNames.bind(styles);

export type packageType = 'Standard package' | 'Premium package' | 'Premium Plus package';

export default function MembershipPage() {
  const [{ user }] = useCookies(['user']);
  const [{ }, setCookie] = useCookies(['packageType']);
  const { width } = useWindowSize();
  const router = useRouter();
  const [isDataLayerSent, setIsDataLayerSent] = useState(false);

  useEffect(() => {
    if (width !== 0 && !isDataLayerSent) {
      // @ts-ignore
      window.dataLayer?.push({
        event: 'homepage or membership page',
        page_url: location.href,
      });
      setIsDataLayerSent(true);
    }
  }, [width, isDataLayerSent]);

  useEffect(() => {
    reCaptcha();
  }, []);

  const isNotMember = width !== 0 && user && !user?.hash;

  const goToSignUp = (type: packageType) => {
    setCookie('packageType', type, { path: '/' });
    router.push('/auth/sign-up');
  };

  const renderPackageElements = (packageItems: PackageItem[], packageType: PackageType) => {
    return packageItems.map((item: PackageItem) => {
      return (
        <PackageItemElement key={item.id} item={item} packageType={packageType} />
      );
    });
  };


  return (
    <div className={cn('membership')}>
      {/* SECTION: HEADER */}
      <section className={cn('header')}>
        <div className={cn('container')}>
          <Breadcrumbs mTop={64} mode="dark" links={[{ title: 'Membership', href: '' }]} />
          <h1 className={cn('header__title')}>Membership Information</h1>
          <div className={cn('header__description')}>
            <p className={cn('header__description-paragraph')}>
              Welcome to DFA! If you are a professional <Link href="/">freight forwarder</Link>, we look forward to you
              joining our network and getting the most out of your business! The Digital Freight Alliance is the logistics
              association created in 2020 by DP World to unite logistics companies in one community, optimizing processes and
              using digital tools for logistics.
            </p>
            <p className={cn('header__description-paragraph')}>
              With Digital Freight Alliance we have three membership options:
            </p>
          </div>
        </div>
      </section>

      {/* SECTION: PACKAGES */}
      <section className={cn('packages')}>
        <div className={cn('container')}>
          {/* STANDARD PACKAGE */}
          <div className={cn('package')}>
            <div className={cn('package__content')}>
              <h3 className={cn('package__title')}>Standard Package</h3>
              <ul className={cn('package__list')}>
                {renderPackageElements(freePackageItems, 'free')}
              </ul>
            </div>
            <div className={cn('package__footer')}>
              <div className={cn('package__price')}>
                <span className={cn('package__price-value', 'free')}>Free</span>
              </div>
              <div className={cn('package__buttons')}>
                {isNotMember
                  ?
                  <button
                    className={cn('package__button-join', 'free')}
                    id="button-submit"
                    type="submit"
                    onClick={() => goToSignUp('Standard package')}
                  >
                    Join now
                  </button>
                  :
                  null
                }
              </div>
            </div>
          </div>

          {/* PREMIUM PACKAGE */}
          <div className={cn('package', 'premium')}>
            <div className={cn('package__background')}>
              <img
                src="/images/pages/Membership/membership_premium_vector1.png"
                alt="Premium Membership for Freight Forwarding Customers"
              />
            </div>
            <div className={cn('package__content')}>
              <h3 className={cn('package__title', 'premium')}>Premium Package</h3>

              <ul className={cn('package__list')}>
                {renderPackageElements(premiumPackageItems, 'premium')}
              </ul>
            </div>
            <div className={cn('package__footer')}>
              <div className={cn('package__price')}>
                <span className={cn('package__price-value', 'premium')}>$3999/HQ (billed annually)</span>
                <CustomTooltip title={'$1999/branch office'} disableInteractive arrow>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M10.1875 2C5.66565 2 2 5.66565 2 10.1875C2 14.7094 5.66565 18.375 10.1875 18.375C14.7094 18.375 18.375 14.7094 18.375 10.1875C18.375 5.66565 14.7094 2 10.1875 2ZM10.1875 16.7375C6.57582 16.7375 3.6375 13.7992 3.6375 10.1875C3.6375 6.57582 6.57582 3.6375 10.1875 3.6375C13.7992 3.6375 16.7375 6.57582 16.7375 10.1875C16.7375 13.7992 13.7992 16.7375 10.1875 16.7375ZM11.5521 12.9167H11.0063V9.91458C11.0063 9.46257 10.6395 9.09583 10.1875 9.09583H9.09583C8.64382 9.09583 8.27708 9.46257 8.27708 9.91458C8.27708 10.3666 8.64382 10.7333 9.09583 10.7333H9.36875V12.9167H8.82292C8.3709 12.9167 8.00417 13.2834 8.00417 13.7354C8.00417 14.1874 8.3709 14.5542 8.82292 14.5542H11.5521C12.0041 14.5542 12.3708 14.1874 12.3708 13.7354C12.3708 13.2834 12.0041 12.9167 11.5521 12.9167ZM10.1875 8.00417C10.7904 8.00417 11.2792 7.51537 11.2792 6.9125C11.2792 6.30956 10.7904 5.82083 10.1875 5.82083C9.58456 5.82083 9.09583 6.30956 9.09583 6.9125C9.09583 7.51537 9.58456 8.00417 10.1875 8.00417Z"
                      fill="#E0E4EC"
                    />
                  </svg>
                </CustomTooltip>
              </div>
              <div className={cn('package__buttons')}>
                {isNotMember
                  ?
                  <button
                    className={cn('package__button-join', 'premium')}
                    id="button-submit"
                    type="submit"
                    onClick={() => goToSignUp('Premium package')}
                  >
                    Join now
                  </button>
                  :
                  null
                }
              </div>
            </div>
          </div>

          {/* PREMIUM PLUS PACKAGE */}
          <div className={cn('package', 'premium')}>
            <div className={cn('package__background')}>
              <img
                src="/images/pages/Membership/membership_premium_vector1.png"
                alt="Premium Membership for Freight Forwarding Customers"
              />
            </div>

            <div className={cn('package__content')}>
              <h3 className={cn('package__title', 'premium')}>Premium Plus Package</h3>

              <ul className={cn('package__list')}>
                {renderPackageElements(premiumPlusPackageItems, 'premium')}
              </ul>
            </div>
            <div className={cn('package__footer')}>
              <div className={cn('package__price')}>
                <span className={cn('package__price-value', 'premium')}>$4999/HQ (billed annually)</span>
                <CustomTooltip title={'$2499/branch office'} disableInteractive arrow>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M10.1875 2C5.66565 2 2 5.66565 2 10.1875C2 14.7094 5.66565 18.375 10.1875 18.375C14.7094 18.375 18.375 14.7094 18.375 10.1875C18.375 5.66565 14.7094 2 10.1875 2ZM10.1875 16.7375C6.57582 16.7375 3.6375 13.7992 3.6375 10.1875C3.6375 6.57582 6.57582 3.6375 10.1875 3.6375C13.7992 3.6375 16.7375 6.57582 16.7375 10.1875C16.7375 13.7992 13.7992 16.7375 10.1875 16.7375ZM11.5521 12.9167H11.0063V9.91458C11.0063 9.46257 10.6395 9.09583 10.1875 9.09583H9.09583C8.64382 9.09583 8.27708 9.46257 8.27708 9.91458C8.27708 10.3666 8.64382 10.7333 9.09583 10.7333H9.36875V12.9167H8.82292C8.3709 12.9167 8.00417 13.2834 8.00417 13.7354C8.00417 14.1874 8.3709 14.5542 8.82292 14.5542H11.5521C12.0041 14.5542 12.3708 14.1874 12.3708 13.7354C12.3708 13.2834 12.0041 12.9167 11.5521 12.9167ZM10.1875 8.00417C10.7904 8.00417 11.2792 7.51537 11.2792 6.9125C11.2792 6.30956 10.7904 5.82083 10.1875 5.82083C9.58456 5.82083 9.09583 6.30956 9.09583 6.9125C9.09583 7.51537 9.58456 8.00417 10.1875 8.00417Z"
                      fill="#E0E4EC"
                    />
                  </svg>
                </CustomTooltip>
              </div>
              <div className={cn('package__buttons')}>
                {isNotMember
                  ?
                  <button
                    className={cn('package__button-join', 'premium')}
                    id="button-submit"
                    type="submit"
                    onClick={() => goToSignUp('Premium Plus package')}
                  >
                    Join now
                  </button>
                  :
                  null
                }
              </div>
            </div>
          </div>
        </div>
      </section> 

      {/* SECTION: ASSOCIATION */}
      <section className={cn('association')}>
        <div className={cn('container')}>
          <div className={cn('association__paragraphs')}>
            <p className={cn('association__paragraph')}>
              Either option is the start of your journey with an innovative
              association supported by DP World, a leading enabler of logistics and trade.
              Start your subscription by completing the form via button "Join now".
            </p>
            <p className={cn('association__paragraph')}>
              By signing in or creating an account,
              you agree with our <Link href="/">Terms & Conditions</Link> and <Link href="/">Privacy Policy</Link>.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

export const getStaticProps = async () => {
  return {
    props: {
      title: 'Join DF Alliance Membership Today | DF Alliance',
      description:
        'Become a DF Alliance member and gain access to an extensive network of logistics professionals, digital tools, and global shipping resources.',
      keywords:
        'freight alliance membership, freight alliance membership cost, freight membership cost, membership freight forwarder, logistics membership, digital freight alliance membership',
      path: '/membership',
    },
  };
};

MembershipPage.getLayout = function getLayout(page: ReactElement) {
  return <RootLayout>{page}</RootLayout>;
};
